import { Form } from "@inertiajs/react"
import { Play, Sparkles } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { formatRelativeTime } from "@/lib/relative-time"
import {
  dismissCreatorSpottedPostPath,
  submitCreatorSpottedPostPath,
} from "@/routes"
import type { SpottedPost } from "@/types/collab"

/**
 * OKS-505 (§2d-C) — Spotted-post confirm sheet (`ui/drawer` bottom sheet).
 *
 * A SIBLING OVERLAY mounted on Creator/Missions — NOT a remount of the §09
 * picker (the OKS-34/35/391/445 remount class must NOT regress; memory
 * `crew_section_relocation_remounts_picker`). It opens when the page carries a
 * `spotted_post` prop (the `?spotted=<id>` deep-link from the
 * `creator.mission.post_spotted` notification), and renders ENTIRELY from the
 * frozen snapshot — zero request-path TikTok calls.
 *
 * ## Actions (Crew form rule — Inertia `<Form>`, no shadcn Form / RHF / zod)
 *
 *   - **Submit this post** → POST `submitCreatorSpottedPostPath`. On success
 *     the server redirects to Crew Home with NO `?spotted` param → the prop
 *     goes null → this sheet unmounts; `flash.success` toasts globally
 *     (`useFlash`). On failure the server redirects back to `?spotted=<id>`
 *     with `inertia: { errors }` → the prop is unchanged so the sheet STAYS
 *     mounted (open state preserved on the same record id), and `flash.error`
 *     toasts the `submissions.errors.*` code. Pending UI only (the `<Form>`
 *     `processing` flag) — NO localStorage, NO client-side optimism.
 *   - **Not this one** → POST `dismissCreatorSpottedPostPath`. Silent
 *     (server redirects home with no param + no flash); the record is marked
 *     `dismissed` and never re-surfaces.
 *   - **Swipe-down / overlay tap** → closes locally WITHOUT dismissing (the
 *     record stays `notified`; re-openable from the bell). Distinguished from
 *     "Not this one" — only that button sets `dismissed`.
 */
export interface SpottedPostSheetProps {
  /** The detected-post snapshot, or null/undefined when there is no sheet. */
  spottedPost?: SpottedPost | null
}

export function SpottedPostSheet({ spottedPost }: SpottedPostSheetProps) {
  const { t, i18n } = useTranslation()

  // Open-state derived during render (no effect-sync): the sheet is open for a
  // present spotted record UNLESS the creator manually closed THAT record's
  // sheet (swipe / overlay tap). A submit-FAILURE re-render keeps the same id
  // and `closedId` stays null, so the sheet stays open (KD-7); a new spotted id
  // arriving naturally re-opens (its id !== closedId). Manual close stamps
  // `closedId` WITHOUT mutating the record (stays `notified`, re-openable from
  // the bell) — only "Not this one" persists `dismissed`.
  const [closedId, setClosedId] = useState<number | null>(null)
  const open = Boolean(spottedPost) && spottedPost?.id !== closedId

  const handleOpenChange = (next: boolean) => {
    if (!next && spottedPost) setClosedId(spottedPost.id)
  }

  if (!spottedPost) return null

  const { id, handle, thumbnail_url, caption_excerpt, view_count, like_count } =
    spottedPost

  const metricParts: string[] = []
  if (spottedPost.posted_at) {
    metricParts.push(
      t("missions.spotted.metrics.posted", {
        when: formatRelativeTime(spottedPost.posted_at, t, i18n.language),
      }),
    )
  }
  if (view_count !== null) {
    metricParts.push(
      t("missions.spotted.metrics.views", {
        value: formatCompactNumber(view_count),
      }),
    )
  }
  if (like_count !== null) {
    metricParts.push(
      t("missions.spotted.metrics.likes", {
        value: formatCompactNumber(like_count),
      }),
    )
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent data-testid="spotted-post-sheet">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <Sparkles className="text-primary size-5" aria-hidden="true" />
            {t("missions.spotted.title")}
          </DrawerTitle>
          <DrawerDescription>
            {t("missions.spotted.description", { handle: handle ?? "" })}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4">
          <div className="bg-muted flex gap-3 rounded-md p-3">
            <div className="bg-muted-foreground/10 relative aspect-[72/100] w-[72px] shrink-0 overflow-hidden rounded-md">
              {thumbnail_url && (
                <img
                  src={thumbnail_url}
                  alt={caption_excerpt ?? t("missions.spotted.thumbnail_alt")}
                  className="size-full object-cover"
                />
              )}
              {view_count !== null && (
                <span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5 text-[10px] font-medium text-white">
                  <Play className="size-2.5 fill-current" aria-hidden="true" />
                  {formatCompactNumber(view_count)}
                </span>
              )}
            </div>

            <div className="flex min-w-0 flex-col justify-center gap-1">
              {caption_excerpt && (
                <p className="text-foreground truncate text-sm font-medium">
                  {caption_excerpt}
                </p>
              )}
              {metricParts.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  {metricParts.join(" · ")}
                </p>
              )}
            </div>
          </div>
        </div>

        <DrawerFooter>
          <Form
            action={submitCreatorSpottedPostPath(id)}
            method="post"
            options={{ preserveScroll: true }}
          >
            {({ processing }) => (
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
                data-testid="spotted-submit"
              >
                {t("missions.spotted.submit_cta")}
              </Button>
            )}
          </Form>

          <Form
            action={dismissCreatorSpottedPostPath(id)}
            method="post"
            options={{ preserveScroll: true }}
          >
            {({ processing }) => (
              <Button
                type="submit"
                variant="ghost"
                className="w-full"
                disabled={processing}
                data-testid="spotted-dismiss"
              >
                {t("missions.spotted.dismiss_cta")}
              </Button>
            )}
          </Form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
